/**
 * Token encryption/decryption using Web Crypto API (AES-GCM).
 * A device-specific key is generated once and stored in IndexedDB
 * (not localStorage), so XSS attacks reading localStorage cannot
 * decrypt the tokens without also accessing IndexedDB.
 *
 * On Android WebViews where IndexedDB or crypto.subtle may be
 * unavailable (private browsing, restricted WebView, etc.),
 * falls back gracefully to plaintext storage.
 */

const DB_NAME = 'mycue_keystore';
const DB_STORE = 'keys';
const KEY_ID = 'device_encryption_key';

/** Check if the required Web Crypto + IndexedDB APIs are available */
function isCryptoAvailable(): boolean {
  try {
    return (
      typeof indexedDB !== 'undefined' &&
      typeof crypto !== 'undefined' &&
      typeof crypto.subtle !== 'undefined' &&
      typeof crypto.subtle.generateKey === 'function'
    );
  } catch {
    return false;
  }
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        try {
          req.result.createObjectStore(DB_STORE);
        } catch {
          // Store already exists or DB error — ignore
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
}

async function getOrCreateKey(): Promise<CryptoKey> {
  const db = await openDB();

  // Try to load existing key
  const existing = await new Promise<CryptoKey | undefined>((resolve, reject) => {
    try {
      const tx = db.transaction(DB_STORE, 'readonly');
      const req = tx.objectStore(DB_STORE).get(KEY_ID);
      req.onsuccess = () => resolve(req.result as CryptoKey | undefined);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });

  if (existing) {
    db.close();
    return existing;
  }

  // Generate new AES-GCM key
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable
    ['encrypt', 'decrypt']
  );

  // Store in IndexedDB
  await new Promise<void>((resolve, reject) => {
    try {
      const tx = db.transaction(DB_STORE, 'readwrite');
      const req = tx.objectStore(DB_STORE).put(key, KEY_ID);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });

  db.close();
  return key;
}

/**
 * Encrypt a plaintext string. Returns a base64-encoded string
 * containing the IV + ciphertext.
 */
export async function encryptToken(plaintext: string): Promise<string> {
  // If crypto APIs unavailable, return plaintext (graceful degradation)
  if (!isCryptoAvailable()) return plaintext;

  try {
    const key = await getOrCreateKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plaintext);
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    );
    // Combine IV + ciphertext
    const combined = new Uint8Array(iv.length + new Uint8Array(ciphertext).length);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);
    return btoa(String.fromCharCode(...combined));
  } catch {
    // Fallback: return plaintext if crypto fails (e.g. in tests or restricted WebView)
    return plaintext;
  }
}

/**
 * Decrypt a base64-encoded string (IV + ciphertext) back to plaintext.
 */
export async function decryptToken(encrypted: string): Promise<string> {
  // If crypto APIs unavailable, assume token is plaintext
  if (!isCryptoAvailable()) return encrypted;

  try {
    const key = await getOrCreateKey();
    const combined = Uint8Array.from(atob(encrypted), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    );
    return new TextDecoder().decode(decrypted);
  } catch {
    // Check if the input looks like a plain (unencrypted) Supabase refresh token.
    // Supabase tokens are base64url strings ~30-60 chars, containing only [A-Za-z0-9_-].
    // Our encrypted tokens are longer base64 with potential '+' and '/'.
    if (/^[A-Za-z0-9_-]{20,80}$/.test(encrypted)) {
      return encrypted; // likely a legacy plain token
    }
    // Decryption truly failed — return empty so callers know the token is invalid
    console.warn('Token decryption failed, token is unrecoverable');
    return '';
  }
}
